import React, { FC, useContext } from 'react';
import { Formik } from 'formik';
import {
  FormModalHeader,
  FormModalContent,
  FormModalFooter,
  FormModalUnderWritingErrors,
} from 'Shared/components';
import { useApi, useLazyApi } from 'Shared/services/Api';
import { PolicyLevelCoverages } from './PolicyLevelCoverages';
import { VehicleLevelCoverages } from './VehicleLevelCoverages';
import { ModalLoading, ModalError } from '@clearcover/atlas';
import {
  CoverageOptionsResponse,
  CoveredPartyPayloadType,
  EditCoveragesResponseType,
} from '@clearcover/core-app-types';
import { useParams, useHistory } from 'react-router-dom';
import { draftEndpointModifier, PolicyContext } from 'Policies/utils';
import { useUnderwritingErrors } from 'Shared/hooks/useUnderwritingErrors';
import * as Yup from 'yup';

import { editPolicyCoveragePermissionsPolicy } from 'Admin/authPolicies';

type VehiclesCoverages = Record<
  string,
  {
    display: string;
    selectedCoverages: Record<string, string>;
    selectedEndorsements: Record<string, string>;
  }
>;

export type EditCoveragesFormProps = {
  close: () => void;
  vehicles: VehiclesCoverages;
  selectedPolicyCoverages: Record<string, string>;
  selectedPolicyEndorsements: Record<string, string>;
};

export type EditCoveragesFormValuesType = {
  vehiclesCoverages: Record<
    string,
    {
      coverages: Record<string, string>;
      endorsements: Record<string, string>;
    }
  >;
  policyCoverages: {
    coverages: Record<string, string>;
    endorsements: Record<string, string>;
  };
};

const objectKeysRequired = (keys: Array<string>) =>
  Yup.object(
    keys.reduce(
      (acc, item) => ({
        ...acc,
        [item]: Yup.string().required('Required'),
      }),
      {}
    )
  );

export const EditCoveragesForm: FC<EditCoveragesFormProps> = ({
  close,
  vehicles,
  selectedPolicyCoverages,
  selectedPolicyEndorsements,
}) => {
  const { id: policyId, draftId } = useParams<{
    id: string;
    draftId: string;
  }>();

  const history = useHistory();
  const {
    details,
    flags,
    refetchHandler,
    canEndorseDuringRegionalEvents,
    resourceEffectiveGrants,
  } = useContext(PolicyContext);
  const { underwritingErrors, setUnderwritingErrors, hasUnderwritingErrors } =
    useUnderwritingErrors();
  const [{ data, loading, error }] = useApi<CoverageOptionsResponse>(
    `/options/coverages?identifier=${details.filingIdentifier}`
  );

  const [, editCoverages] = useLazyApi<EditCoveragesResponseType>({
    method: 'POST',
    url: `/policies/${policyId}/editCoverages${draftEndpointModifier(
      details.draftType,
      flags.renewalQuotesEnabled
    )}`,
  });

  const hasParentCoverage = data?.policy?.endorsements?.some(
    (endorsement) => 'parentCoverage' in endorsement
  );

  const canEditPolicyCoverages =
    canEndorseDuringRegionalEvents &&
    editPolicyCoveragePermissionsPolicy(resourceEffectiveGrants);

  if (loading) {
    return <ModalLoading data-testid="coverage-modal-loading" />;
  }

  if (error) {
    return (
      <ModalError
        data-testid="edit-coverages-error"
        errorMessage={error.message}
        componentStack={error.stack}
      />
    );
  }

  if (details.region === 'US-TX') {
    data.vehicle.coverages = data.vehicle.coverages.map((coverage) => {
      if (coverage.identifier === 'UMPD') {
        const coverageOptionsWithOutDeductible = Object.keys(
          coverage.coverageOptions
        )
          .filter((key) => key.includes('+D') || key === 'waived')
          .reduce((obj: Record<string, string>, key) => {
            obj[key] = coverage.coverageOptions[key];
            return obj;
          }, {});

        return {
          ...coverage,
          coverageOptions: coverageOptionsWithOutDeductible,
        };
      }
      return coverage;
    });
  }

  if (hasParentCoverage) {
    // use the coverage options response to update the selected policy endorsements
    // if the selected policy endorsements are not found in the coverage options response, we will use the waived endorsement
    selectedPolicyEndorsements = data.policy.endorsements
      .map((endorsement) => endorsement.identifier)
      .reduce((acc, endorsement) => {
        acc[endorsement] = selectedPolicyEndorsements[endorsement] || 'waived';
        return acc;
      }, {} as any);
  }

  const submitEditCoveragesForm = async ({
    values,
    setStatus,
    setSubmitting,
    bypassUnderwriting,
  }: {
    values: CoveredPartyPayloadType;
    setStatus: (arg0: any) => void;
    setSubmitting: (arg0: boolean) => void;
    bypassUnderwriting?: boolean;
  }) => {
    const payload = {
      ...values,
      ...(bypassUnderwriting ? { bypassUnderwriting: true } : {}),
    };

    return editCoverages({ data: payload })
      .then((draft) => {
        draftId === draft.data.draftId
          ? refetchHandler()
          : history.push(`/policies/${policyId}/draft/${draft.data.draftId}`);
      })
      .catch((status) => {
        setUnderwritingErrors(status);
        setStatus(hasUnderwritingErrors(status) ? '' : status.message);
        setSubmitting(false);
      });
  };

  return (
    <Formik
      initialValues={{
        policyCoverages: {
          coverages: selectedPolicyCoverages,
          endorsements: selectedPolicyEndorsements,
        },
        vehiclesCoverages: Object.keys(vehicles).reduce(
          (acc, v) => ({
            ...acc,
            [v]: {
              coverages: vehicles[v].selectedCoverages,
              endorsements: vehicles[v].selectedEndorsements,
            },
          }),
          {}
        ),
      }}
      onSubmit={(values, { setStatus, setSubmitting }) =>
        submitEditCoveragesForm({
          values,
          setStatus,
          setSubmitting,
          bypassUnderwriting: false,
        })
      }
      validationSchema={Yup.object().shape({
        policyCoverages: Yup.object({
          coverages: objectKeysRequired(Object.keys(selectedPolicyCoverages)),
          endorsements: objectKeysRequired(
            Object.keys(selectedPolicyEndorsements)
          ),
        }),
        vehiclesCoverages: Yup.object({
          ...Object.keys(vehicles).reduce(
            (acc, v) => ({
              ...acc,
              [v]: Yup.object({
                coverages: objectKeysRequired(
                  Object.keys(vehicles[v].selectedCoverages)
                ),
                endorsements: objectKeysRequired(
                  Object.keys(vehicles[v].selectedEndorsements)
                ),
              }),
            }),
            {}
          ),
        }),
      })}
      validateOnMount
    >
      {({ values, setStatus, setSubmitting }) => (
        <>
          <FormModalHeader>Edit Coverages</FormModalHeader>
          <FormModalContent>
            <PolicyLevelCoverages
              coverages={data.policy.coverages}
              endorsements={data.policy.endorsements}
              disabled={!canEditPolicyCoverages}
              hasParentCoverage={hasParentCoverage}
            />
            {Object.keys(vehicles).map((identifier) => (
              <VehicleLevelCoverages
                key={identifier}
                coverageOptions={data.vehicle.coverages}
                endorsementOptions={data.vehicle.endorsements}
                identifier={identifier}
                display={vehicles[identifier].display}
              />
            ))}
          </FormModalContent>
          <FormModalUnderWritingErrors
            errors={underwritingErrors}
            grants={resourceEffectiveGrants}
            submitIgnoringUnderwriting={() =>
              submitEditCoveragesForm({
                values,
                setStatus,
                setSubmitting,
                bypassUnderwriting: true,
              })
            }
          />
          <FormModalFooter identifier="edit-coverages" close={close} />
        </>
      )}
    </Formik>
  );
};

EditCoveragesForm.displayName = 'EditCoveragesForm';
